import React from "react";
import logoMark from "../assets/logo-mark.png";

// Format a YYYY-MM-DD string as "Jul 17, 2026" for the printed report.
const fmtLongDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmtGeneratedAt = () => {
  const d = new Date();
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

// Full-page letterhead printout of every winner for the given year. Rendered
// into an off-screen container and swapped into view by the `@media print`
// stylesheet when the user hits "Print winners".
export const WinnersPrintView = ({ year, drawings }) => {
  const winners = (drawings || [])
    .filter((d) => d.winning_ticket != null)
    .sort((a, b) => (a.drawing_date || "").localeCompare(b.drawing_date || ""));

  return (
    <>
      {/* Print-only stylesheet — hides the app and shows just this letterhead. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #winners-print-root, #winners-print-root * { visibility: visible !important; }
          #winners-print-root {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            display: block !important;
            background: #fff !important;
          }
          @page { size: letter landscape; margin: 0.5in; }
        }
      `}</style>

      <div
        id="winners-print-root"
        style={{ position: "fixed", left: "-10000px", top: 0 }}
        aria-hidden="true"
      >
        <div
          style={{
            padding: "0.4in 0.5in",
            fontFamily: "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            color: "#0B1214",
            background: "#FFFFFF",
            width: "10in",
          }}
        >
          {/* Header letterhead */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "0.3in" }}>
            <tbody>
              <tr>
                <td style={{ verticalAlign: "top", width: "0.9in" }}>
                  <img src={logoMark} alt="Red Hook Elks Lodge" style={{ height: "0.75in", width: "auto" }} />
                </td>
                <td style={{ verticalAlign: "top", paddingLeft: "0.15in" }}>
                  <div style={{ fontFamily: "'Lora', ui-serif, Georgia, serif", fontSize: "20pt", fontWeight: 700, lineHeight: 1.1 }}>
                    400 Club Raffle &mdash; Winners
                  </div>
                  <div style={{ fontSize: "11pt", marginTop: "0.05in", color: "#3D4B4E" }}>
                    Red Hook Rhinebeck Elks Lodge #2022 &middot; {year}
                  </div>
                </td>
                <td style={{ verticalAlign: "top", textAlign: "right", fontSize: "9pt", color: "#657174" }}>
                  Generated<br />{fmtGeneratedAt()}
                </td>
              </tr>
            </tbody>
          </table>

          {winners.length === 0 ? (
            <div style={{ padding: "0.5in", textAlign: "center", color: "#657174", fontSize: "12pt" }}>
              No winners drawn yet for {year}.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10.5pt" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #122E36", textAlign: "left" }}>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E" }}>Drawing</th>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E" }}>Date</th>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E", textAlign: "right" }}>Ticket #</th>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E" }}>Winner</th>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E" }}>Phone</th>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E", textAlign: "right" }}>Prize</th>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E", textAlign: "center" }}>Paid</th>
                  <th style={{ padding: "0.08in 0.12in", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3D4B4E", textAlign: "right" }}>Pool</th>
                </tr>
              </thead>
              <tbody>
                {winners.map((d, idx) => (
                  <tr
                    key={d.id}
                    style={{
                      borderBottom: "1px solid #D9CFBC",
                      background: idx % 2 === 0 ? "#FFFFFF" : "#F7F4EC",
                    }}
                  >
                    <td style={{ padding: "0.09in 0.12in", fontWeight: 600 }}>{d.name}</td>
                    <td style={{ padding: "0.09in 0.12in" }}>{fmtLongDate(d.drawing_date)}</td>
                    <td style={{ padding: "0.09in 0.12in", textAlign: "right", fontWeight: 700, color: "#122E36", fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>#{d.winning_ticket}</td>
                    <td style={{ padding: "0.09in 0.12in" }}>{d.winning_buyer || "\u2014"}</td>
                    <td style={{ padding: "0.09in 0.12in", fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: "10pt" }}>
                      {d.winning_phone || "\u2014"}
                    </td>
                    <td style={{ padding: "0.09in 0.12in", textAlign: "right", fontWeight: 700 }}>${Number(d.prize_amount || 0).toFixed(2)}</td>
                    <td style={{ padding: "0.09in 0.12in", textAlign: "center", fontSize: "14pt", lineHeight: 1, color: d.prize_paid ? "#0F7A3F" : "#3D4B4E" }} title={d.prize_paid ? "Paid" : "Not paid"}>
                      {d.prize_paid ? "\u2611" : "\u2610"}
                    </td>
                    <td style={{ padding: "0.09in 0.12in", textAlign: "right", color: "#657174" }}>{d.eligible_count ?? "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ padding: "0.12in 0.12in 0", fontSize: "10pt", fontWeight: 700, textAlign: "right" }}>
                    Total prizes paid ({winners.filter((d) => d.prize_paid).length} of {winners.length} drawing{winners.length === 1 ? "" : "s"}):
                  </td>
                  <td style={{ padding: "0.12in 0.12in 0", fontSize: "10pt", fontWeight: 700, textAlign: "right", color: "#122E36" }}>
                    ${winners.filter((d) => d.prize_paid).reduce((sum, d) => sum + Number(d.prize_amount || 0), 0).toFixed(2)}
                  </td>
                  <td colSpan={2} style={{ padding: "0.12in 0.12in 0", fontSize: "9pt", textAlign: "right", color: "#657174" }}>
                    of ${winners.reduce((sum, d) => sum + Number(d.prize_amount || 0), 0).toFixed(2)} awarded
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {/* Footer */}
          <div style={{ marginTop: "0.4in", paddingTop: "0.15in", borderTop: "1px solid #D9CFBC", fontSize: "9pt", color: "#657174", lineHeight: 1.5 }}>
            Red Hook Rhinebeck Elks Lodge #2022 &middot; 7711 Albany Post Road, Red Hook, NY 12571
            <br />
            This report was generated from the 400 Club Ticket Tracker on {fmtGeneratedAt()}.
          </div>
        </div>
      </div>
    </>
  );
};
